<div class="d-flex flex-column conversation-widget">
    <!-- Read-only identity header + manual refresh -->
    <div class="d-flex align-items-center gap-2 px-3 py-2 border-bottom flex-shrink-0">
        <div class="flex-grow-1 min-w-0">
            <div class="fw-semibold text-truncate">{{ contact()?.name }}</div>
            <div class="small text-muted">
                @if (contact()?.phoneNumber) {
                    <i class="bi bi-telephone me-1"></i>
                    {{ contact()?.phoneNumber | phone }}
                } @else {
                    <span class="fst-italic">
                        {{ { feature: "Customer Messaging", component: "Conversation Widget", label: "No phone number on file" } | translate }}
                    </span>
                }
            </div>
        </div>
        <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            [title]="{ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'Refresh conversation' } | translate"
            (click)="refresh()"
            [disabled]="refreshing()"
        >
            <i
                class="bi"
                [class.bi-arrow-clockwise]="!refreshing()"
                [class.spinner-border]="refreshing()"
                [class.spinner-border-sm]="refreshing()"
            ></i>
        </button>
    </div>

    <!-- Scrollable history, oldest -> newest -->
    <div #history class="flex-grow-1 overflow-auto px-3 py-3 history">
        @for (m of thread(); track m.id) {
            <div class="d-flex mb-3" [class.justify-content-end]="m.direction === 'Outbound'">
                <div class="d-flex flex-column msg-col">
                    <div
                        class="message-bubble p-2 rounded shadow-sm"
                        [class.bg-primary]="m.direction === 'Outbound'"
                        [class.text-white]="m.direction === 'Outbound'"
                        [class.bg-body-secondary]="m.direction === 'Inbound'"
                    >
                        @if (m.direction === 'Outbound' && m.sentByUserName) {
                            <div class="sender-name x-small fw-semibold mb-1">{{ m.sentByUserName }}</div>
                        }

                        @if (isMms(m)) {
                            <div class="d-flex flex-wrap gap-2 mb-1">
                                @for (a of m.attachments; track a.id) {
                                    @if (a.status === 'Stored' && a.url) {
                                        <img
                                            [src]="a.url"
                                            [alt]="a.name || ({ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'photo' } | translate)"
                                            class="rounded mms-thumb"
                                            role="button"
                                            tabindex="0"
                                            [title]="{ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'Download photo' } | translate"
                                            (click)="download(a)"
                                            (keydown.enter)="download(a)"
                                        />
                                    } @else if (a.status === 'Pending') {
                                        <div
                                            class="mms-thumb rounded border d-flex flex-column align-items-center justify-content-center bg-body-tertiary text-muted"
                                        >
                                            <span class="spinner-border spinner-border-sm mb-1"></span>
                                            <span class="x-small">
                                                {{ { feature: "Customer Messaging", component: "Conversation Widget", label: "Loading photo…" } | translate }}
                                            </span>
                                        </div>
                                    } @else {
                                        <div
                                            class="mms-thumb rounded border d-flex flex-column align-items-center justify-content-center bg-body-tertiary text-muted"
                                        >
                                            <i class="bi bi-image fs-3"></i>
                                            <span class="x-small">
                                                {{ { feature: "Customer Messaging", component: "Conversation Widget", label: "Image unavailable" } | translate }}
                                            </span>
                                        </div>
                                    }
                                }
                            </div>
                        }

                        @if (m.body) {
                            <div class="message-body">{{ m.body }}</div>
                        }

                        <div class="d-flex align-items-center gap-2 mt-1" [class.justify-content-end]="m.direction === 'Outbound'">
                            <span class="x-small" [class.text-white-50]="m.direction === 'Outbound'" [class.text-muted]="m.direction === 'Inbound'">
                                {{ m.sentDate | date: "short" }}
                            </span>
                            @if (m.direction === 'Outbound' && m.status) {
                                @if (m.status.name === 'Failed') {
                                    <span class="badge text-bg-danger x-small">
                                        <i class="bi bi-exclamation-triangle-fill me-1"></i>
                                        {{ statusLabel(m.status.name) }}
                                    </span>
                                } @else {
                                    <span class="x-small text-white-50" [attr.title]="readByTooltip(m)">
                                        <i class="bi me-1" [class.bi-check2]="!m.readDate" [class.bi-check2-all]="!!m.readDate"></i>
                                        {{ statusLabel(m.status.name) }}
                                    </span>
                                }
                            }
                        </div>

                        @if (m.direction === 'Outbound' && m.failureReason) {
                            <div class="x-small text-danger mt-1 d-flex align-items-center gap-1">
                                <i class="bi bi-info-circle"></i>
                                {{ m.failureReason }}
                            </div>
                        }
                    </div>

                    @if (showOriginLink(m)) {
                        <a
                            href="javascript:void(0)"
                            class="origin-link small mt-1 align-self-start"
                            (click)="goToOrigin(m)"
                            [title]="originLabelFor(m)"
                        >
                            <i class="bi bi-box-arrow-up-right me-1"></i>
                            {{ originLabelFor(m) }}
                        </a>
                    }
                </div>
            </div>
        } @empty {
            <div class="text-center text-muted py-5">
                <i class="bi bi-chat-dots fs-1 d-block mb-2"></i>
                {{ { feature: "Customer Messaging", component: "Conversation Widget", label: "No messages yet. Start the conversation below." } | translate }}
            </div>
        }
    </div>

    <!-- Composer -->
    <div class="border-top p-2 flex-shrink-0 composer">
        @switch (gate()) {
            @case ('feature-off') {
                <div class="alert alert-warning py-2 px-3 mb-2 small d-flex align-items-center gap-2">
                    <i class="bi bi-toggle-off"></i>
                    {{ gateText() }}
                </div>
            }
            @case ('no-consent') {
                <div class="alert alert-danger py-2 px-3 mb-2 small d-flex align-items-center gap-2">
                    <i class="bi bi-shield-x"></i>
                    {{ gateText() }}
                </div>
            }
            @case ('no-phone') {
                <div class="alert alert-danger py-2 px-3 mb-2 small d-flex align-items-center gap-2">
                    <i class="bi bi-telephone-x"></i>
                    {{ gateText() }}
                </div>
            }
        }

        @if (sendError()) {
            <div class="alert alert-danger py-2 px-3 mb-2 small d-flex align-items-center gap-2">
                <i class="bi bi-exclamation-triangle"></i>
                {{ sendError() }}
            </div>
        }

        @if (pendingAttachments().length > 0) {
            <div class="d-flex flex-wrap gap-2 mb-2">
                @for (a of pendingAttachments(); track a.id) {
                    <div class="position-relative preview-thumb">
                        <img
                            [src]="a.url!"
                            [alt]="a.name || ({ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'attachment' } | translate)"
                            class="rounded border w-100 h-100"
                        />
                        <button
                            type="button"
                            class="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 preview-remove"
                            (click)="removeAttachment(a.id)"
                            [title]="{ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'Remove' } | translate"
                        >
                            <i class="bi bi-x"></i>
                        </button>
                    </div>
                }
            </div>
        }

        <div class="d-flex align-items-center gap-2">
            @if (canSendMedia()) {
                <button
                    type="button"
                    class="btn btn-outline-secondary flex-shrink-0"
                    [title]="{ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'Attach image' } | translate"
                    (click)="fileInput.click()"
                    [disabled]="!canSend() || sending()"
                >
                    <i class="bi bi-paperclip"></i>
                </button>
                <input #fileInput type="file" accept="image/*" class="d-none" (change)="onAttachSelected($event)" />
            }
            <textarea
                #composerInput
                class="form-control composer-input flex-grow-1"
                rows="1"
                [placeholder]="{ feature: 'Customer Messaging', component: 'Conversation Widget', label: 'Type a message…' } | translate"
                [ngModel]="draft()"
                (ngModelChange)="draft.set($event)"
                (keydown.enter)="onComposerEnter($event)"
                [disabled]="!canSend() || sending()"
            ></textarea>
            <button
                type="button"
                class="btn btn-primary flex-shrink-0"
                (click)="send()"
                [disabled]="!canSend() || !hasContent() || overLimit() || sending()"
            >
                <i class="bi" [class.bi-send]="!sending()" [class.spinner-border]="sending()" [class.spinner-border-sm]="sending()"></i>
            </button>
        </div>
        <div class="d-flex justify-content-end mt-1">
            <span class="x-small" [class.text-danger]="overLimit()" [class.text-muted]="!overLimit()">{{ draft().length }} / {{ maxLength }}</span>
        </div>
    </div>
</div>
